import React, { useMemo } from 'react';
import '../styles/GammaPanel.css';

const CRORE = 1e7;

// ── Condition row helper ───────────────────────────────────────────────────────
const Cond = ({ met, label, bear = false }) => (
  <div className={`gsd-cond ${met ? (bear ? 'gsd-cond-met-bear' : 'gsd-cond-met') : 'gsd-cond-unmet'}`}>
    <span className="gsd-cond-icon">{met ? '✓' : '✗'}</span> {label}
  </div>
);

const GammaPanel = ({ gammaData, gammaFlipCrossover }) => {
  const {
    spotPrice,
    gammaFlipLevel,
    totalNetGEX,
    marketRegime,
    maxGEXStrike,
    hasValidData,
    bbMiddle,
    bbUpper,
    bbLower,
    bbPercentB,
    bbSignal,
    callWallStrike,
    putWallStrike,
    highVolumeStrike,
    strikeData: strikeDataRaw,
    volumeAlerts: volumeAlertsRaw,
    // Gamma setup detection
    gammaSetup,
    gammaSetupScore,
    putBuildupBelowSpot,
    callCoveringAboveSpot,
    netGexPositive,
    callBuildupAboveSpot,
    putUnwindingBelowSpot,
  } = gammaData || {};
  const strikeData   = strikeDataRaw   ?? [];
  const volumeAlerts = volumeAlertsRaw ?? [];

  // ── Select ±10 strikes nearest to spot, sorted descending (highest on top) ─
  const visibleStrikes = useMemo(() => {
    if (!strikeData.length) return [];
    const sorted = [...strikeData].sort(
      (a, b) => Math.abs(a.strikePrice - spotPrice) - Math.abs(b.strikePrice - spotPrice)
    );
    const nearest = sorted.slice(0, 21);
    return nearest.sort((a, b) => b.strikePrice - a.strikePrice);
  }, [strikeData, spotPrice]);

  const maxAbsGEX = useMemo(
    () => Math.max(
      ...visibleStrikes.map((s) => Math.max(Math.abs(s.callGEX || 0), Math.abs(s.putGEX || 0))),
      1
    ),
    [visibleStrikes]
  );

  // ATM = strike in data closest to spot
  const atmStrike = useMemo(() => {
    if (!strikeData.length) return null;
    return strikeData.reduce((prev, cur) =>
      Math.abs(cur.strikePrice - spotPrice) < Math.abs(prev.strikePrice - spotPrice) ? cur : prev
    ).strikePrice;
  }, [strikeData, spotPrice]);

  const spikeStrikes = useMemo(() => {
    const set = new Set();
    volumeAlerts.forEach(a => set.add(a.strikePrice));
    return set;
  }, [volumeAlerts]);

  if (!gammaData) return null;

  // Gamma Zone signal — where is spot relative to the walls?
  const step = visibleStrikes.length > 1
    ? (visibleStrikes[0].strikePrice - visibleStrikes[visibleStrikes.length - 1].strikePrice)
      / (visibleStrikes.length - 1)
    : 50;
  const zoneSignal = (() => {
    if (!callWallStrike || !putWallStrike || !spotPrice) return null;
    const toCall = callWallStrike - spotPrice;
    const toPut  = spotPrice - putWallStrike;
    if (toCall <= 0) return { label: 'Above Call Wall — Gamma Acceleration ↑', color: '#00c853' };
    if (toPut  <= 0) return { label: 'Below Put Wall — Gamma Acceleration ↓',  color: '#b71c1c' };
    if (toCall <= step) return { label: 'At Call Wall — Watch for Breakout ↑',  color: '#ff9800' };
    if (toPut  <= step) return { label: 'At Put Wall — Watch for Breakdown ↓',  color: '#ff9800' };
    if (toCall < toPut) return { label: 'Closer to Call Wall — Mild Resistance', color: '#fbc02d' };
    return                    { label: 'Closer to Put Wall — Mild Support',      color: '#fbc02d' };
  })();

  const isLong = marketRegime === 'Long Gamma';
  const spotAboveFlip = spotPrice > gammaFlipLevel;

  const fmtGEX = (v) => {
    if (v == null) return 'N/A';
    const cr = v / CRORE;
    return (cr >= 0 ? '+' : '') + cr.toFixed(2) + ' Cr';
  };

  const fmtPrice = (v) => (v != null ? Number(v).toFixed(0) : 'N/A');

  return (
    <div className="gamma-panel">
      <h3 className="gamma-title">Dealer Gamma Exposure (GEX)</h3>

      {!hasValidData ? (
        <div className="gamma-no-data">
          Implied Volatility (IV) data not available for this expiry.
          GEX requires live NSE IV data — not present in mock / non-trading sessions.
        </div>
      ) : (
        <>
          {/* ── Gamma flip crossover alert ───────────────────────────────── */}
          {gammaFlipCrossover && (
            <div className={`gamma-flip-crossover-alert gamma-flip-crossover-${gammaFlipCrossover === 'UP' ? 'up' : 'down'}`}>
              <span className="gamma-flip-crossover-icon">{gammaFlipCrossover === 'UP' ? '▲' : '▼'}</span>
              <span className="gamma-flip-crossover-text">
                GAMMA FLIP CROSSOVER — Spot crossed {gammaFlipCrossover === 'UP' ? 'ABOVE' : 'BELOW'} flip level
                {gammaFlipCrossover === 'UP'
                  ? '. Dealers switching to long gamma — expect directional acceleration upward'
                  : '. Dealers switching to short gamma — expect directional acceleration downward'}
              </span>
              <span className="gamma-flip-crossover-flip">Flip: {fmtPrice(gammaFlipLevel)}</span>
            </div>
          )}

          {/* ── Gamma Setup Detection ────────────────────────────────────── */}
          {gammaSetup && (
            <div className={`gsd-card gsd-card-${gammaSetup === 'GAMMA_SQUEEZE' ? 'bull' : gammaSetup === 'GAMMA_ACCELERATION' ? 'bear' : 'neutral'}`}>
              <div className="gsd-header">
                <span className="gsd-label">Gamma Setup · Step 5</span>
                <span className={`gsd-badge gsd-badge-${gammaSetup === 'GAMMA_SQUEEZE' ? 'bull' : gammaSetup === 'GAMMA_ACCELERATION' ? 'bear' : 'neutral'}`}>
                  {gammaSetup === 'GAMMA_SQUEEZE'      ? 'GAMMA SQUEEZE ↑'
                   : gammaSetup === 'GAMMA_ACCELERATION' ? 'GAMMA ACCELERATION ↓'
                   : 'NEUTRAL'}
                </span>
                <span className="gsd-score">{gammaSetupScore ?? 0}/3 conditions</span>
              </div>

              <div className="gsd-body">
                {/* Bull setup conditions — spotAboveGammaFlip removed (counted in confluence, not here) */}
                <div className="gsd-col">
                  <div className="gsd-col-title gsd-col-bull">Bullish Squeeze</div>
                  <Cond met={putBuildupBelowSpot}   label="Put writing below spot" />
                  <Cond met={callCoveringAboveSpot} label="Call writers covering" />
                  <Cond met={netGexPositive}         label="Net GEX positive" />
                </div>
                {/* Bear setup conditions */}
                <div className="gsd-col">
                  <div className="gsd-col-title gsd-col-bear">Bearish Acceleration</div>
                  <Cond met={callBuildupAboveSpot}   label="Call writing above spot" bear />
                  <Cond met={putUnwindingBelowSpot}  label="Put writers exiting"    bear />
                  <Cond met={!netGexPositive}         label="Net GEX negative"       bear />
                </div>
              </div>

              <div className="gsd-desc">
                {gammaSetup === 'GAMMA_SQUEEZE'
                  ? 'Put writers below spot + call writer covering → dealers hedge by buying futures → price accelerates higher.'
                  : gammaSetup === 'GAMMA_ACCELERATION'
                  ? 'Call writers above spot + put writers exiting → dealers hedge by selling futures → downside accelerates.'
                  : 'No dominant gamma setup. Watch OI changes as spot approaches call/put walls.'}
              </div>
            </div>
          )}

          {/* ── Summary row ─────────────────────────────────────────────── */}
          <div className="gamma-summary">
            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Market Regime</span>
              <span
                className="gamma-summary-value sentiment-pill"
                style={{ backgroundColor: isLong ? '#00e676' : '#e53935' }}
              >
                {marketRegime}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Gamma Flip</span>
              <span className="gamma-summary-value">{fmtPrice(gammaFlipLevel)}</span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Spot vs Flip</span>
              <span
                className="gamma-summary-value sentiment-pill"
                style={{ backgroundColor: spotAboveFlip ? '#00e676' : '#e53935' }}
              >
                {spotAboveFlip ? 'Above (stable)' : 'Below (volatile)'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Total Net GEX</span>
              <span
                className="gamma-summary-value"
                style={{ color: totalNetGEX >= 0 ? '#00c853' : '#d32f2f' }}
              >
                {fmtGEX(totalNetGEX)}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Max GEX Strike</span>
              <span className="gamma-summary-value">{fmtPrice(maxGEXStrike)}</span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">BB Upper</span>
              <span className="gamma-summary-value gamma-bb-upper">
                {bbUpper != null ? fmtPrice(bbUpper) : 'Waiting…'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">BB Mid</span>
              <span className="gamma-summary-value">
                {bbMiddle != null ? fmtPrice(bbMiddle) : 'Waiting…'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">BB Lower</span>
              <span className="gamma-summary-value gamma-bb-lower">
                {bbLower != null ? fmtPrice(bbLower) : 'Waiting…'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">%B</span>
              <span className="gamma-summary-value">
                {bbPercentB != null ? (bbPercentB * 100).toFixed(1) + '%' : 'Waiting…'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">BB Signal</span>
              <span
                className="gamma-summary-value sentiment-pill"
                style={{
                  backgroundColor:
                    bbSignal === 'Bullish' ? '#00e676' :
                    bbSignal === 'Bearish' ? '#e53935' : '#ff9800',
                }}
              >
                {bbMiddle != null ? bbSignal : 'Waiting…'}
              </span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Call Wall</span>
              <span className="gamma-summary-value gamma-callwall-value">{fmtPrice(callWallStrike)}</span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">Put Wall</span>
              <span className="gamma-summary-value gamma-putwall-value">{fmtPrice(putWallStrike)}</span>
            </div>

            <div className="gamma-summary-item">
              <span className="gamma-summary-label">High Volume Strike</span>
              <span className="gamma-summary-value">{fmtPrice(highVolumeStrike)}</span>
            </div>

            {zoneSignal && (
              <div className="gamma-summary-item">
                <span className="gamma-summary-label">Gamma Zone</span>
                <span
                  className="gamma-summary-value sentiment-pill"
                  style={{ backgroundColor: zoneSignal.color }}
                >
                  {zoneSignal.label}
                </span>
              </div>
            )}
          </div>

          {/* ── Bidirectional bar chart ──────────────────────────────────── */}
          <div className="gamma-chart">
            {/* Column headers */}
            <div className="gamma-chart-header">
              <span className="gamma-col-strike">Strike</span>
              <span className="gamma-col-left-label">← Put GEX</span>
              <span className="gamma-col-divider" />
              <span className="gamma-col-right-label">Call GEX →</span>
              <span className="gamma-col-value">Net GEX</span>
            </div>

            {visibleStrikes.map((s) => {
              const isAtm      = s.strikePrice === atmStrike;
              const halfStep   = visibleStrikes.length > 1
                ? (visibleStrikes[0].strikePrice - visibleStrikes[visibleStrikes.length - 1].strikePrice) / visibleStrikes.length / 2 + 1
                : 1;
              const isFlip     = gammaFlipLevel    != null && Math.abs(s.strikePrice - gammaFlipLevel)    < halfStep;
              const isBBUpper  = bbUpper            != null && Math.abs(s.strikePrice - bbUpper)            < halfStep;
              const isBBLower  = bbLower            != null && Math.abs(s.strikePrice - bbLower)            < halfStep;
              const isCallWall = callWallStrike    != null && Math.abs(s.strikePrice - callWallStrike)    < halfStep;
              const isPutWall  = putWallStrike     != null && Math.abs(s.strikePrice - putWallStrike)     < halfStep;
              const isHighVol  = highVolumeStrike  != null && Math.abs(s.strikePrice - highVolumeStrike)  < halfStep;
              const isVolSpike = spikeStrikes.has(s.strikePrice);
              const putPct     = Math.min((Math.abs(s.putGEX  || 0) / maxAbsGEX) * 100, 100);
              const callPct    = Math.min((Math.abs(s.callGEX || 0) / maxAbsGEX) * 100, 100);
              const isPos      = s.netGEX >= 0;

              return (
                <div
                  key={s.strikePrice}
                  className={[
                    'gamma-row',
                    isAtm      ? 'gamma-row-atm'       : '',
                    isFlip     ? 'gamma-row-flip'      : '',
                    isBBUpper  ? 'gamma-row-bb-upper'  : '',
                    isBBLower  ? 'gamma-row-bb-lower'  : '',
                    isCallWall ? 'gamma-row-call-wall' : '',
                    isPutWall  ? 'gamma-row-put-wall'  : '',
                    isVolSpike ? 'gamma-row-volspike'  : '',
                  ].join(' ').trim()}
                >
                  <span className="gamma-col-strike">
                    {s.strikePrice}
                    {isAtm      && <span className="gamma-badge atm-badge">ATM</span>}
                    {isFlip     && <span className="gamma-badge flip-badge">FLIP</span>}
                    {isBBUpper  && <span className="gamma-badge bb-upper-badge">BB-HI</span>}
                    {isBBLower  && <span className="gamma-badge bb-lower-badge">BB-LO</span>}
                    {isCallWall && <span className="gamma-badge callwall-badge">C-WALL</span>}
                    {isPutWall  && <span className="gamma-badge putwall-badge">P-WALL</span>}
                    {isHighVol  && <span className="gamma-badge highvol-badge">VOL</span>}
                    {isVolSpike && <span className="gamma-badge volspike-badge">VOL!</span>}
                  </span>

                  {/* Left zone — Put GEX (always shown) */}
                  <div className="gamma-col-left">
                    <div
                      className="gamma-bar gamma-bar-neg"
                      style={{ width: `${putPct}%` }}
                      title={`Put GEX: ${fmtGEX(s.putGEX)}`}
                    />
                  </div>

                  <div className="gamma-col-divider" />

                  {/* Right zone — Call GEX (always shown) */}
                  <div className="gamma-col-right">
                    <div
                      className="gamma-bar gamma-bar-pos"
                      style={{ width: `${callPct}%` }}
                      title={`Call GEX: ${fmtGEX(s.callGEX)}`}
                    />
                  </div>

                  <span
                    className="gamma-col-value"
                    style={{ color: isPos ? '#00c853' : '#d32f2f' }}
                  >
                    {fmtGEX(s.netGEX)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── Interpretation note ─────────────────────────────────────── */}
          <div className="gamma-legend">
            <span className="gamma-legend-item">
              <span className="gamma-legend-dot" style={{ background: '#00e676' }} />
              Long Gamma: dealers absorb moves → market stabilises
            </span>
            <span className="gamma-legend-item">
              <span className="gamma-legend-dot" style={{ background: '#e53935' }} />
              Short Gamma: dealers amplify moves → larger swings expected
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default GammaPanel;
